import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronLeft, BookOpen, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import docsData from '../../data/docs.json';
import FlexSearch from 'flexsearch';

// Category badge colors
const CATEGORY_STYLES = {
  'Getting Started': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Understanding Higlo': 'bg-blue-50 text-blue-700 border-blue-200',
  'Troubleshooting': 'bg-amber-50 text-amber-700 border-amber-200',
  'FAQ': 'bg-purple-50 text-purple-700 border-purple-200',
};

function CategoryBadge({ category }) {
  const style = CATEGORY_STYLES[category] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${style}`}>
      {category}
    </span>
  );
}

// Build a lookup from filename fragments to articles for internal link navigation
const articleLookup = {};
docsData.articles.forEach(article => {
  // Index by slug filename (e.g. "hand-gesture-guide")
  const filename = article.slug.split('/').pop();
  articleLookup[filename] = article;
  articleLookup[filename + '.md'] = article;
  // Also index by full relative paths used in markdown links
  articleLookup[article.slug + '.md'] = article;
  articleLookup[article.slug] = article;
});

function resolveArticleLink(href) {
  if (!href || !href.endsWith('.md')) return null;
  // Strip leading ../ segments and try to match
  const cleaned = href.replace(/^(\.\.\/)+/, '');
  // Try full path match, then just the filename
  return articleLookup[cleaned] || articleLookup[cleaned.split('/').pop()] || null;
}

function DocumentationViewer({ onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  // Navigation history stack — top of stack is the current article (or null = browse view)
  const [history, setHistory] = useState([null]);
  const selectedArticle = history[history.length - 1];
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const scrollRef = useRef(null);

  // Build FlexSearch index once
  const searchIndex = useMemo(() => {
    const index = new FlexSearch.Index({
      tokenize: 'forward',
      resolution: 9,
    });
    docsData.articles.forEach((article, i) => {
      const searchable = [
        article.title,
        article.category,
        ...(article.keywords || []),
        ...(article.tags || []),
        article.searchText
      ].join(' ');
      index.add(i, searchable);
    });
    return index;
  }, []);

  // Search handler
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const results = searchIndex.search(searchQuery, { limit: 20 });
    setSearchResults(results.map(i => docsData.articles[i]));
  }, [searchQuery, searchIndex]);

  // Focus search on open
  useEffect(() => {
    const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to top when article changes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [selectedArticle]);

  const goBack = useCallback(() => {
    setHistory(h => (h.length > 1 ? h.slice(0, -1) : h));
  }, []);

  // Escape key: back or close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedArticle) goBack();
        else onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedArticle, goBack, onClose]);

  const navigateToArticle = useCallback((article) => {
    setHistory(h => [...h, article]);
    setSearchQuery('');
  }, []);

  // Markdown components — uses navigateToArticle for internal links
  const mdComponents = useMemo(() => ({
    h1: ({ children }) => <h1 className="text-xl font-bold text-slate-900 mt-4 mb-2 first:mt-0">{children}</h1>,
    h2: ({ children }) => <h2 className="text-lg font-semibold text-slate-800 mt-4 mb-1.5 pb-1 border-b border-slate-100">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-semibold text-slate-700 mt-3 mb-1">{children}</h3>,
    h4: ({ children }) => <h4 className="text-sm font-semibold text-slate-700 mt-2 mb-0.5">{children}</h4>,
    p: ({ children }) => <p className="text-sm text-slate-600 leading-relaxed mb-2">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5 text-sm text-slate-600">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5 text-sm text-slate-600">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-3 border-slate-200 pl-3 my-2 text-sm text-slate-500 italic">{children}</blockquote>
    ),
    code: ({ inline, children }) =>
      inline !== false && typeof children === 'string' && !children.includes('\n') ? (
        <code className="bg-slate-100 text-slate-700 text-xs px-1 py-0.5 rounded">{children}</code>
      ) : (
        <pre className="bg-slate-800 text-slate-100 text-xs p-3 rounded-lg overflow-x-auto mb-2">
          <code>{children}</code>
        </pre>
      ),
    a: ({ href, children }) => {
      const linkedArticle = resolveArticleLink(href);
      if (linkedArticle) {
        return (
          <button
            onClick={(e) => { e.preventDefault(); navigateToArticle(linkedArticle); }}
            className="text-blue-600 hover:text-blue-800 underline underline-offset-2 font-medium"
          >
            {children}
          </button>
        );
      }
      return <span className="text-slate-600">{children}</span>;
    },
    table: ({ children }) => (
      <div className="overflow-x-auto mb-2">
        <table className="w-full text-sm text-left border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-slate-100">{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => <th className="px-3 py-2 text-xs font-semibold text-slate-700 whitespace-nowrap">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 text-sm text-slate-600">{children}</td>,
    hr: () => <hr className="my-3 border-slate-100" />,
  }), [navigateToArticle]);

  // Articles to display
  const displayArticles = useMemo(() => {
    if (searchQuery.trim()) return searchResults;
    if (selectedCategory) return docsData.articles.filter(a => a.category === selectedCategory);
    return docsData.articles;
  }, [searchQuery, searchResults, selectedCategory]);

  // Group by category
  const articlesByCategory = useMemo(() => {
    const groups = {};
    for (const article of displayArticles) {
      if (!groups[article.category]) groups[article.category] = [];
      groups[article.category].push(article);
    }
    return groups;
  }, [displayArticles]);

  const isSearching = searchQuery.trim().length > 0;

  return createPortal(
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col" style={{ zIndex: 9999 }}>
      {/* Header — same structure in both states so it doesn't shift */}
      <div className="bg-slate-800 text-white px-4 py-3 flex-shrink-0">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2 h-9">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {selectedArticle ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                className="text-white hover:bg-slate-700 -ml-2 h-9 px-2 text-sm flex items-center gap-0.5 shrink-0"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="leading-none">Back</span>
              </Button>
            ) : (
              <div className="h-9 w-9 flex items-center justify-center shrink-0">
                <BookOpen className="h-5 w-5" />
              </div>
            )}
            <h1 className="text-base font-semibold truncate leading-tight">
              {selectedArticle ? selectedArticle.title : 'Knowledge Base'}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-slate-700 h-9 w-9 p-0 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {selectedArticle ? (
        /* Article view */
        <div className="flex-1 overflow-y-auto" ref={scrollRef}>
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="mb-3">
              <CategoryBadge category={selectedArticle.category} />
            </div>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {selectedArticle.content}
            </ReactMarkdown>
          </div>
        </div>
      ) : (
        /* Browse / Search view */
        <>
          {/* Search bar */}
          <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex-shrink-0">
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 placeholder-slate-400"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Category filter pills */}
              {!isSearching && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                      selectedCategory === null
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    All
                  </button>
                  {docsData.categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                      className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                        selectedCategory === cat
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Article list */}
          <div className="flex-1 overflow-y-auto px-4 py-3" ref={scrollRef}>
            <div className="max-w-3xl mx-auto space-y-4">
              {isSearching ? (
                searchResults.length > 0 ? (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                    </p>
                    <div className="space-y-1.5">
                      {searchResults.map(article => (
                        <ArticleCard key={article.slug} article={article} onClick={() => navigateToArticle(article)} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No results found for "{searchQuery}"</p>
                    <p className="text-xs text-slate-400 mt-1">Try different keywords or browse by category</p>
                  </div>
                )
              ) : (
                Object.entries(articlesByCategory).map(([category, articles]) => (
                  <div key={category}>
                    <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                      {category}
                    </h2>
                    <div className="space-y-1.5">
                      {articles.map(article => (
                        <ArticleCard
                          key={article.slug}
                          article={article}
                          onClick={() => navigateToArticle(article)}
                          showCategory={false}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

function ArticleCard({ article, onClick, showCategory = true }) {
  const preview = useMemo(() => {
    const lines = article.content.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('---') && !trimmed.startsWith('![') && !trimmed.startsWith('```') && trimmed.length > 30) {
        return trimmed
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1')
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
          .replace(/`([^`]+)`/g, '$1')
          .slice(0, 120) + (trimmed.length > 120 ? '...' : '');
      }
    }
    return '';
  }, [article.content]);

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-lg border border-slate-200 px-3 py-2.5 hover:border-slate-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-slate-800 group-hover:text-slate-900">
            {article.title}
          </h3>
          {preview && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{preview}</p>
          )}
          {showCategory && (
            <div className="mt-1">
              <CategoryBadge category={article.category} />
            </div>
          )}
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500 flex-shrink-0" />
      </div>
    </button>
  );
}

export default DocumentationViewer;
